import { useMemo, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
  PaginationState,
  SortingState,
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';

import { GroupMembership } from '@aslanpay/portal-common-lib';

import Table from 'app/components/UI/Table/Table';
import { defaultPageSize } from 'app/constants/table';
import useGroups from 'app/hooks/useGroups';

interface PotEmployeesTableProps {
  companyId: string;
  groupId: string;
}

interface EmployeeRow {
  firstName: string;
  lastName: string;
  email: string;
}

function toEmployeeRow(membership: GroupMembership): EmployeeRow {
  const parts = (membership.individualName ?? '').split(' ');
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' ') || '',
    email: membership.individualEmail,
  };
}

const columnHelper = createColumnHelper<EmployeeRow>();

const columns: ColumnDef<EmployeeRow, unknown>[] = [
  columnHelper.accessor('firstName', {
    header: 'First name',
    enableSorting: true,
    cell: (info: CellContext<EmployeeRow, unknown>) => <Text>{info.getValue() as string}</Text>,
  }) as ColumnDef<EmployeeRow, unknown>,
  columnHelper.accessor('lastName', {
    header: 'Last name',
    enableSorting: true,
    cell: (info: CellContext<EmployeeRow, unknown>) => <Text>{info.getValue() as string}</Text>,
  }) as ColumnDef<EmployeeRow, unknown>,
  columnHelper.accessor('email', {
    header: 'Email',
    enableSorting: true,
    cell: (info: CellContext<EmployeeRow, unknown>) => (
      <Text color="brand.purple">{info.getValue() as string}</Text>
    ),
  }) as ColumnDef<EmployeeRow, unknown>,
];

function PotEmployeesTable({ companyId, groupId }: PotEmployeesTableProps) {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data } = useGroups.useFetchGroupMemberships(
    companyId,
    groupId,
    undefined,
    !!companyId && !!groupId
  );

  const allMembers = useMemo<EmployeeRow[]>(() => {
    const pages = data?.pages ?? [];
    return pages.flatMap((p) => p.list).map(toEmployeeRow);
  }, [data]);

  const sorted = useMemo(() => {
    if (!sorting.length) return allMembers;
    const { id, desc } = sorting[0];
    return [...allMembers].sort((a, b) => {
      const aColumnValue = String((a as unknown as Record<string, unknown>)[id] ?? '');
      const bColumnValue = String((b as unknown as Record<string, unknown>)[id] ?? '');
      return desc
        ? bColumnValue.localeCompare(aColumnValue)
        : aColumnValue.localeCompare(bColumnValue);
    });
  }, [allMembers, sorting]);

  const paginated = useMemo(
    () => sorted.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [sorted, pageIndex, pageSize]
  );

  return (
    <Box minH={{ base: '300px', md: '400px' }} display="flex" flexDir="column" width="100%">
      <Table<EmployeeRow>
        columns={columns}
        data={paginated}
        total={sorted.length}
        pageNumber={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </Box>
  );
}

export default PotEmployeesTable;
